<template>
    <BasePanel 
        headline='lang_PUBLISHING_CAPTION_WORKS'
        :alvqa="alvqa"
    >
        <!-- 
            item-key must be used here because 1 work can be in the list multiple times, 
            so the id of the work is not unique to be the key. we are using the index of the items here.
         -->
        <template v-slot:panelContent> 
            <v-data-table
                :headers="headers"
                :items="listItems"
                item-key="index"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :items-per-page="itemsPerPage"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row class="pb-3">
                        <v-spacer></v-spacer>
                        <v-col cols="3">
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

            <!-- HEADERS -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- ITEMS -->
                <template v-slot:[`item.title`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.title"
                            :alvqa="alvqa + '-list-row-' + item.alvId + '-title'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.composers`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.composers.join(' / ')"
                            :alvqa="alvqa + '-list-row-' + item.alvId + '-composers'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.publishers`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="item.publishers.join(' / ')"
                            :alvqa="alvqa + '-list-row-' + item.alvId + '-publishers'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.gemaNumber`]="{ item }">
                    <div :alvqa="alvqa +'-list-row-' + item.alvId + '-text-gema-number'">
                        {{ item.gemaNumber }}
                    </div>
                </template>
                <template v-slot:[`item.workSheetSeconds`]="{ item }">
                    <div :alvqa="alvqa +'-list-row-' + item.alvId + '-text-work-sheet-seconds'">
                        {{ 
                            item.workSheetSeconds !== null ? 
                            $alvPlugin.transformSecondsToDuration(item.workSheetSeconds) + ' (' + (item.workSheetSeconds) + ')' :
                            '' 
                        }}
                    </div>
                </template>
                <template v-slot:[`item.workSetlistSeconds`]="{ item }">
                    <div :alvqa="alvqa +'-list-row-' + item.alvId + '-text-work-setlist-seconds'">
                        {{ 
                            item.workSetlistSeconds !== null ? 
                            $alvPlugin.transformSecondsToDuration(item.workSetlistSeconds) + ' (' + (item.workSetlistSeconds) + ')' :
                            '' 
                        }}
                    </div>
                </template>
                <template v-slot:[`item.workAvType`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="$t(getWorkAvType(item.workAvType))"
                            :alvqa="alvqa +'-list-row-' + item.alvId + '-text-work-av-type'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.workSetlistType`]="{ item }">
                    <div :alvqa="alvqa +'-list-row-' + item.alvId + '-text-work-setlist-type'">
                        {{ getSetlistType(item.workSetlistType) }}
                    </div>
                </template>
                <template v-slot:[`item.workCharacteristicType`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="$t(getWorkCharacteristicType(item.workCharacteristicType))"
                            :alvqa="alvqa +'-list-row-' + item.alvId + '-text-work-characteristic-type'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.bigRight`]="{ item }">
                    <div :alvqa="alvqa +'-list-row-' + item.alvId + '-text-big-right'">
                        {{ getBigRight(item.bigRight) }}
                    </div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="$t(getStatus(item.status))"
                            :alvqa="alvqa +'-list-row-' + item.alvId + '-text-status'"
                        />
                    </div>
                </template>

                <!-- Buttons -->
                 <!-- 
                    index must be used here because 1 work can be in the list multiple times, 
                    so the id of the work is not unique to be the key,
                    we need to set it for finding the correct entry later in the edit dialog.
                    we are using the index of the items here.
                -->
                <template v-slot:[`item.delete`]="{ index }">
                    <BaseIcon 
                        @click="deleteRow(index)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-' + index + '-icon-delete'"
                    />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item, index }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-' + index + '-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item, index)"
                    />
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>

<script>
import 
    { 
        DropdownPublishingWorksStatus,
        DropdownPublishingSheetsWorkAvType,
        DropdownPublishingSheetsWorkCharacteristicType,
        DropdownGenericYesNo,
        DropdownPublishingSheetsWorkSetlistType
    } 
from '@/assets/dropdowns/alvDropdowns.js'

import BasePanel from '@/components/_base/BasePanel'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseTooltip from '@/components/_base/BaseTooltip'

export default {
    name: "WorksPanel",
    components: {
        BasePanel,
        BaseIcon,
        BaseButton,
        BaseTooltip
    }, 
    data() {
        return {
            alvqa: 'publishing-sheets-edit-form-panel-works'
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t('lang_PUBLISHING_SHEETS_CAPTION_WORK_TITLE'), 
                    value: 'title', 
                    align: 'left', 
                    sortable: false,
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-title"
                },
                {
                    text: this.$t('lang_PUBLISHING_CAPTION_COMPOSERS'),
                    value: 'composers',
                    align: 'left', 
                    sortable: false, 
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-composers"
                },
                {
                    text: this.$t('lang_PUBLISHING_CAPTION_PUBLISHERS'), 
                    value: 'publishers',
                    align: 'left', 
                    sortable: false, 
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-publishers"
                },
                {
                    text: this.$t('lang_PUBLISHING_WORKS_CAPTION_WORK_ID'), 
                    value: 'gemaNumber', 
                    align: 'left', 
                    sortable: false,
                    width: "140px",
                    // class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-gema-number"
                },
                {
                    text: this.$t('lang_PUBLISHING_WORKS_CAPTION_DURATION'), 
                    value: this.sheetType === 'film/tv' ? 'workSheetSeconds' : 'workSetlistSeconds',
                    align: 'left', 
                    sortable: false, 
                    width: "120px",
                    // class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-work-sheet-seconds"
                }, 
                {
                    text: this.$t("lang_PUBLISHING_SHEETS_CAPTION_TYPE"), 
                    value: this.sheetType === 'film/tv' ? 'workAvType' : 'workSetlistType', 
                    align: 'left', 
                    sortable: false, 
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-work-av-type"
                }, 
                {
                    text: this.$t(this.sheetType === 'film/tv' ? "lang_PUBLISHING_SHEETS_CAPTION_CHARACTERISTIC" : "lang_GENERAL_CAPTION_TYPE_OF_RIGHTS_BIG_RIGHT"),
                    value: this.sheetType === 'film/tv' ? 'workCharacteristicType' : 'bigRight', 
                    align: 'left', 
                    sortable: false, 
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-work-characteristic-type"
                }, 
                {
                    text: this.$t('lang_PUBLISHING_WORKS_CAPTION_STATUS'), 
                    value: 'status',
                    align: 'left', 
                    sortable: false, 
                    width: "100px",
                    // class: 'text-no-wrap',
                    alvqa: this.alvqa + "-header-status"
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    // class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    // class: 'text-no-wrap'
                }
            ]
        },
        
        listItems() { return this.$store.getters.getSheetsWorks },
        isLoading() { return this.$store.getters.getIsLoading },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        sheetType() { return this.$store.getters.getSheetDetailsType },
    },
    methods: {      
        addNewRow() {
            // this.$store.commit('setShowSearchDialog', true)
            this.$store.commit('setShowConnectWorksToSheetDialog', true)
        },
		deleteRow(index) {
            // set the selected row index, to reuse in delete function which entry should be deleted
            this.$store.commit('setSelectedSheetWork', index)
            this.$store.commit('setDeletionDialogContext', 'SelectedSheetWork')
            this.$store.commit('setShowDeletionDialog', true)
		},
        editInlineFunction(item, index) {
            this.$store.commit('setSelectedSheetsWorks', index)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            // set the copy in dialog details to edit
            this.$store.commit('setSheetsWorksEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setShowEditingDialog', true) 
        },

        getStatus(dbValue) {
            if(dbValue === null) return ''
            let match = DropdownPublishingWorksStatus.find(status => {
                return status.value === dbValue
            })

            if(match !== undefined) return match.text
            else return ''
        },

        getWorkAvType(typeValue) {
            let match = DropdownPublishingSheetsWorkAvType.find(type => {
                return type.value === typeValue
            })

            if(match !== undefined) return match.text
        },
        getSetlistType(typeValue) {
            let match = DropdownPublishingSheetsWorkSetlistType.find(type => {
                return type.value === typeValue
            })
            if(match !== undefined) return match.text
        },

        getWorkCharacteristicType(typeValue) {
            let match = DropdownPublishingSheetsWorkCharacteristicType.find(type => {
                return type.value === typeValue
            })

            if(match !== undefined) return match.text
        },
        getBigRight(bigRightValue) {
            if(bigRightValue !== null && bigRightValue !== undefined) {
                let match = DropdownGenericYesNo.find(element => {
                    return element.value === bigRightValue
                })
                return this.$t(match.text)
            } else {
                return null
            }
        },
    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
</style>